import "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/layerContent.css";

import { Upgrade } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { useTranslation } from "react-i18next";

type Columns = {
  label: string;
  sortAsc: boolean;
};

export default function SelectWebServiceLayers() {
  const darkTheme = defaultStore((state) => state.darkTheme);

  const webServiceLayer = sidebarStore((state) => state.webServiceLayer);
  const webServiceLayersList = sidebarStore(
    (state) => state.webServiceLayersList
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const { t } = useTranslation();

  const [columns, setColumns] = useState<
    { label: string; key: string; sortAsc: boolean }[]
  >([
    { label: "Import", key: "import", sortAsc: false },
    { label: "Layer", key: "title", sortAsc: false },
    { label: "Name", key: "layername", sortAsc: false },
  ]);
  const [layerList, setLayerList] = useState<any[]>([]);
  const [filteredLayers, setFilteredLayers] = useState<any[]>([]);

  const [filterColumn, setFilterColumn] = useState<string>("");
  const [filterSearch, setFilterSearch] = useState<string>("");

  // Sort layers ascending or descending
  const handleFilterAscDesc = (column: {
    label: string;
    key: string;
    sortAsc: boolean;
  }) => {
    setFilteredLayers((prevLayers) => {
      const layersFiltered = [...prevLayers];
      const columnName = column.key.toLowerCase();
      const allObjectsValid = layersFiltered.every(
        (item) => item && item[columnName] !== undefined
      );

      if (allObjectsValid) {
        layersFiltered.sort((x, y) => {
          const xValue = x[columnName];
          const yValue = y[columnName];

          // Handle sorting of boolean values
          // If both xValue and yValue are boolean, sort them based on column.sortAsc
          // If sortAsc is true, sort them in ascending order, otherwise in descending order
          // If values are equal, maintain their relative order
          if (typeof xValue === "boolean" && typeof yValue === "boolean") {
            return column.sortAsc
              ? xValue === yValue
                ? 0
                : xValue
                  ? -1
                  : 1
              : xValue === yValue
                ? 0
                : xValue
                  ? 1
                  : -1;
          }

          // Handle sorting of other types (strings, numbers, etc.)
          return column.sortAsc
            ? xValue.localeCompare(yValue)
            : yValue.localeCompare(xValue);
        });

        column.sortAsc = !column.sortAsc; // Toggle sort direction

        // Return the sorted list
        return layersFiltered;
      } else {
        console.error(
          "Some objects are missing required properties for sorting."
        );
        return prevLayers;
      }
    });
  };

  // Update filtered layers based on search criteria
  useEffect(() => {
    if (filterColumn && filterSearch) {
      const filtered = layerList.filter((item) =>
        item[filterColumn.toLocaleLowerCase()]
          .toLowerCase()
          .includes(filterSearch.toLowerCase())
      );
      setFilteredLayers(filtered);
    } else {
      setFilteredLayers(layerList);
    }
  }, [filterColumn, filterSearch, layerList]);

  // Change import status of layers
  const handleImportInput = (item: any, value: boolean) => {
    const updatedFilteredLayers = filteredLayers.map((layer) =>
      layer === item ? { ...layer, import: value } : layer
    );
    setFilteredLayers(updatedFilteredLayers);

    const updatedLayersList = layerList.map((layer) =>
      layer === item ? { ...layer, import: value } : layer
    );
    setLayerList(updatedLayersList);
  };

  // Check if changes can be saved
  const checkIfCanSave = () => {
    const selectedLayers = filteredLayers.filter((item) => item.import);
    webServiceLayer.layers = selectedLayers;
    changeSaveButtonStateOnAddNewLayers(selectedLayers.length > 0);
  };

  // Update layers list after getting all layers list
  useEffect(() => {
    if (webServiceLayersList && webServiceLayersList.length > 0) {
      const layersList = webServiceLayersList.map((item) => ({
        ...item,
        import: false,
      }));
      setLayerList(layersList);
    }
  }, [webServiceLayersList]);

  // Check if can save after getting all layers list
  useEffect(() => {
    checkIfCanSave();
  }, [filteredLayers]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100% - 16px)"}
      overflow={"auto"}
      paddingY={1}
      gap={3}
    >
      <TextField
        label="Connection Name"
        variant="outlined"
        size="small"
        disabled
        value={webServiceLayer?.name || "Name of connection"}
      />
      <Typography
        fontWeight={600}
        fontSize={20}
        color={darkTheme ? "white" : "darkblue.main"}
      >
        {t("create_layer.add_available_layers")}
      </Typography>
      <Box
        position={"relative"}
        width={"100%"}
        height={"100%"}
        display={"flex"}
        flexDirection={"column"}
        overflow="hidden"
      >
        <Box
          display={"flex"}
          flexDirection="row"
          alignItems={"center"}
          gap={2}
          width="100%"
          position="sticky"
          top={0}
          sx={{
            backgroundColor: darkTheme ? "darkblue.main" : "white",
            borderBottom: darkTheme ? "1px white solid" : "1px #053952 solid",
          }}
        >
          <select
            onChange={(event) => {
              setFilterColumn(event.target.value);
              setFilterSearch("");
            }}
            value={filterColumn}
            id="selectFilterColumn"
            style={{
              padding: "2px 0px",
              border: "none",
              outline: "none",
              width: "auto",
              fontWeight: 500,
              color: darkTheme ? "white" : "#053952",
              backgroundColor: darkTheme ? "#053952" : "white",
              fontSize: "12px",
            }}
          >
            <option key="selectCol" id="selectCol" value="">
              {t("layers_window.feature_table_select_column")}
            </option>
            {columns
              .filter((item) => item.key !== "import")
              .map((item: any, indexCol: number) => (
                <option key={indexCol} value={item.key}>
                  {item.label}
                </option>
              ))}
          </select>
          <InputBase
            disabled={!filterColumn}
            classes={{
              disabled: darkTheme
                ? "infra3d-inputTextDisabledDark"
                : "infra3d-inputTextDisabledLight",
            }}
            placeholder={
              filterColumn === ""
                ? (t("layers_window.feature_table_search_placeholder_before") ??
                  "")
                : (t("layers_window.feature_table_search_placeholder_after") ??
                  "")
            }
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              width: "max-content",
              padding: "0px 4px",
              fontSize: "14px",
            }}
            type={"text"}
            value={filterSearch}
            onChange={(event) => setFilterSearch(event.target.value)}
          />
        </Box>
        <Table
          stickyHeader
          sx={{ display: "block", overflow: "auto", height: "100%" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((item, indexCol) => (
                <TableCell
                  sx={{
                    width: "1%",
                    padding: "4px",
                    backgroundColor: darkTheme ? "darkblue.main" : "white",
                  }}
                  key={indexCol}
                >
                  <Box display={"flex"} alignItems={"center"}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                      }}
                      color={darkTheme ? "white" : "darkblue.main"}
                    >
                      {item.label}
                    </Typography>
                    <IconButton
                      sx={{
                        rotate: item.sortAsc ? "0deg" : "180deg",
                        marginLeft: "8px",
                        transition: "rotate 0.2s",
                        padding: "2px",
                      }}
                      onClick={() => {
                        handleFilterAscDesc(item);
                      }}
                    >
                      <Upgrade />
                    </IconButton>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ height: "100%", overflow: "auto" }}>
            {filteredLayers.map((item: any) => (
              <TableRow
                sx={{
                  "@media (hover: hover)": {
                    ":hover": {
                      backgroundColor: darkTheme
                        ? "primary.light"
                        : "darkblue.light",
                      cursor: "pointer",
                    },
                  },
                }}
                key={`${item.title}_${item.layername}_${item.key}`}
              >
                <TableCell
                  sx={{
                    padding: "4px",
                  }}
                >
                  <input
                    id={darkTheme ? "checkboxButtonDark" : "checkboxButton"}
                    defaultChecked={item.import}
                    onChange={(evt) =>
                      handleImportInput(item, evt.target.checked)
                    }
                    type="checkbox"
                  />
                </TableCell>
                <TableCell
                  sx={{
                    padding: "4px",
                  }}
                >
                  {item.title}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "4px",
                  }}
                >
                  {item.layername}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
